import { CommonModule } from '@angular/common';
import { Component, inject, isSignal } from '@angular/core';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslatePipe } from '@ngx-translate/core';
import { DrawerModule } from 'primeng/drawer';
import { TooltipModule } from 'primeng/tooltip';
import { ModalService } from '../../../../@core/services/modal/modal.service';
import { applySearchRule } from '../../../../@core/utils/search-input-helper';
import { AddressContactLink, AddressContactLinkSearchInput, Condition, Contact, Operator, SearchRule } from '../../../../generated/generated-types';
import { GenericBaseModelDropDown } from '../../../../shared/components/genericBaseModelDropDown';
import { AddressContactLinkEditWidgetComponent } from '../../components/address-contact-link-edit-widget/address-contact-link-edit-widget.component';
import { AddressContactLinkService } from '../../services/address-contact-link.service';
import { ContactHeaderCardWidgetComponent } from '../contact/contact-header-card-widget/contact-header-card-widget.component';

@Component({
  selector: 'app-contact-link-drop-down',
  templateUrl: './contact-link-drop-down.component.html',
  styleUrls: ['./contact-link-drop-down.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    DrawerModule,
    ContactHeaderCardWidgetComponent,
    TooltipModule,
    TranslatePipe,
  ]
})
export class ContactLinkDropDownComponent extends GenericBaseModelDropDown<AddressContactLink> {
  sidebarVisible = false;
  addressContactLinkService = inject(AddressContactLinkService);
  modalService = inject(ModalService);
  constructor(
  ) {
    super(inject(AddressContactLinkService), "AddressContactLink");
    this.createNewPopupComponent = AddressContactLinkEditWidgetComponent;
  }

  override ngOnInit() {
    super.ngOnInit();
    if (this.props?.placeholder) {
      this.placeholder.set(this.i18n.translate(this.props.placeholder));
    }
  }

  buildFilter() {
    if (this.props?.attributes?.parentAddressFieldFilter || this.formState?.detailComponent?.address) {
      let idFilterPerAddressSig: string;
      let idFilterPerFormParentAddressFieldFilter: string;
      if (isSignal(this.formState?.detailComponent?.address))
        idFilterPerAddressSig = this.formState.detailComponent.address()?.id;
      if (this.props?.attributes?.parentAddressFieldFilter)
        idFilterPerFormParentAddressFieldFilter = this.formControl.parent.get(this.props.attributes.parentAddressFieldFilter as string)?.value;
      let idFilter: string = idFilterPerFormParentAddressFieldFilter ?? idFilterPerAddressSig;

      if (!idFilter) { //is this good?
        idFilter = this.model[this.props.attributes.parentAddressFieldFilter];
      }

      if (idFilter) {
        (this.si as AddressContactLinkSearchInput).ofAddressId = idFilter;
      }
    }
    else {
      (this.si as AddressContactLinkSearchInput).ofAddressId = null;
    }

    let inactivatedSearchRules: SearchRule = {
      rules: [
        {
          condition: Condition.OR,
          rules: [
            {
              field: "inactive",
              operator: Operator.equal,
              value: 'false'
            },
            {
              field: "inactive",
              operator: Operator.isnull
            }
          ]
        }
      ]
    }
    applySearchRule(this.si, inactivatedSearchRules);
  }

  override refresh() {
    this.buildFilter();
    return super.refresh();
  }

  showContact() {
    this.sidebarVisible = true;
  }

  override async createNewPopup() {
    if (!this.createNewPopupComponent) {
      console.error('createNewPopupComponent not set');
      return;
    }
    // Get the current address ID using the same logic as buildFilter
    const addressId = this.getCurrentAddressId();
    this.modalService.fromComponent(this.createNewPopupComponent, {
      title: this.i18n.translate(_("common.quick-create-new-entity")),
      closable: true,
      dependencies: {
        component: this.createNewPopupComponent,
      }
    }).subscribe({
      // remeber this is like that, just cuz we had the issue on duplications saved contacts etc..., it a workaround until we full refactor contact link.
      next: async (contactLinkResult: { result: { contact?: Contact }, isDirty: boolean, firstTimeSaveDone: boolean }) => {
        if (contactLinkResult && contactLinkResult.result?.contact?.id) {
          try {
            const contactLink = await this.addressContactLinkService.saveItem({
              contactId: contactLinkResult.result.contact.id, addressId
            });
            this.setModel(contactLink);
          } catch (error) {
            console.error('Error in createNewPopup:', error);
          }
        }
        this.refresh();
      },
      error: (error) => {
        console.error('Error in createNewPopup:', error);
      }
    });
  }

  private getCurrentAddressId(): string | null {
    if (this.props?.attributes?.parentAddressFieldFilter || this.formState?.detailComponent?.address) {
      let idFromSignal: string;
      let idFromFormField: string;
      if (isSignal(this.formState?.detailComponent?.address)) {
        idFromSignal = this.formState.detailComponent.address()?.id;
      }
      if (this.props?.attributes?.parentAddressFieldFilter) {
        idFromFormField = this.formControl.parent.get(
          this.props.attributes.parentAddressFieldFilter as string
        )?.value;
      }
      let addressId = idFromFormField ?? idFromSignal;
      // Fallback to model if not found
      if (!addressId) {
        addressId = this.model[this.props.attributes.parentAddressFieldFilter];
      }
      return addressId;
    }
    return null;
  }
}
